import { useState, useEffect } from "react";
import { useParams, Link } from "react-router";
import PageMeta from "../components/common/PageMeta";
import { tenantService, Tenant } from "../services/tenantService";
import { buildingService } from "../services/buildingService";
import { useBuilding } from "../context/BuildingContext";

export default function TenantList() {
  const { id } = useParams<{ id: string }>();
  const buildingId = id ? parseInt(id) : undefined;
  const { currentBuilding, selectBuilding } = useBuilding();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Sync building context with URL parameter
  useEffect(() => {
    const syncBuilding = async () => {
      if (!buildingId) return;
      if (currentBuilding && currentBuilding.id === buildingId) return;

      try {
        const building = await buildingService.getBuilding(buildingId);
        selectBuilding(building);
      } catch (err) {
        console.error("Error syncing building:", err);
      }
    };

    syncBuilding();
  }, [buildingId, currentBuilding, selectBuilding]);

  useEffect(() => {
    const fetchTenants = async () => {
      if (!buildingId) return;

      setIsLoading(true);
      setError(null);
      try {
        const data = await tenantService.getTenants(buildingId);
        setTenants(data);
      } catch (err) {
        console.error("Error fetching tenants:", err);
        setError("Failed to load tenants. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTenants();
  }, [buildingId]);

  const filteredTenants = tenants.filter((t) =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!buildingId) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-gray-500 dark:text-gray-400">
          Please select a building first
        </p>
      </div>
    );
  }

  return (
    <>
      <PageMeta
        title="Tenants | SBM Package Admin"
        description="Manage tenants for your building"
      />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-800 dark:text-white/90">
              Tenants
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {currentBuilding?.name}
            </p>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Tenant List Card */}
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="flex items-center justify-between border-b border-gray-200 p-5 dark:border-gray-800">
            <h3 className="font-semibold text-gray-800 dark:text-white/90">
              Tenants
            </h3>
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search tenants..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-48 rounded-lg border border-gray-200 bg-white py-1.5 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              />
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
            </div>
          )}

          {/* Table */}
          {!isLoading && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-800">
                    <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Name
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Location
                    </th>
                    <th className="px-5 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      Contacts
                    </th>
                    <th className="px-5 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      Central Delivery
                    </th>
                    <th className="px-5 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                  {filteredTenants.map((tenant) => (
                    <tr
                      key={tenant.id}
                      className="hover:bg-gray-50 dark:hover:bg-white/[0.02]"
                    >
                      <td className="px-5 py-4">
                        <Link
                          to={`/sites/${buildingId}/tenants/${tenant.id}`}
                          className="font-medium text-gray-800 hover:text-brand-500 dark:text-white/90"
                        >
                          {tenant.name}
                        </Link>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-500">
                        {tenant.location || "—"}
                      </td>
                      <td className="px-5 py-4 text-center text-sm text-gray-500">
                        {tenant.contacts_count ?? 0}
                      </td>
                      <td className="px-5 py-4 text-center">
                        {tenant.central_delivery_enabled ? (
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                            Enabled
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                            Off
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-center">
                        {tenant.status === 1 ? (
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                            Archived
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredTenants.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-8 text-center">
                        <svg
                          className="mx-auto h-12 w-12 text-gray-300"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                        <p className="mt-4 text-gray-500 dark:text-gray-400">
                          {searchQuery
                            ? "No tenants match your search"
                            : "No tenants found for this building"}
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
